import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';


interface AIChatBoxProps {
  isOpen: boolean;
  onClose: () => void;
  language: string;
}

interface MessageItem {
  id: number;
  sender: 'user' | 'ai';
  text: string;
}

export default function AIChatBox({ isOpen, onClose, language }: AIChatBoxProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>('');
  const [messages, setMessages] = useState<MessageItem[]>(() => {
  const savedMessages = localStorage.getItem('ai_chat_history');
    return savedMessages ? JSON.parse(savedMessages) : [];
  });
  // const [messages, setMessages] = useState<MessageItem[]>([
  //   { id: 1, sender: 'ai', text: t('ai_chatbox.placeholder') }
  // ]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  // MODAL
  const [contentModal, setContentModal] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [footerModal, setFooterModal] = useState(true);

  useEffect(() => {
    setMessages((prev) => {
      // Empty history, load the initial message
      if (prev.length === 0) {
        return [{ id: 1, sender: 'ai', text: t('ai_chatbox.placeholder') }];
      }
      // Translate the initial message in position 0
      if (prev[0].id === 1) {
        const updated = [...prev];
        updated[0] = { ...updated[0], text: t('ai_chatbox.placeholder') };
        return updated;
      }
      return prev;
    });
  }, [language, t]);

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('ai_chat_history', JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('ai_chat_open', isOpen ? 'true' : 'false');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length > 0) {
      const timer = setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [messages, isOpen]);

  if (!isOpen) return null;

  const resetHistory = () => {
    localStorage.removeItem('ai_chat_history');
    setMessages([{ id: 1, sender: 'ai', text: t('ai_chatbox.placeholder') }]);
  };

  const handleClearHistory = () => {
    setFooterModal(false);
    setContentModal(
      <>
        <Modal.Header closeButton>
          <Modal.Title>{t('ai_chatbox.clear_history')}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>{t('ai_chatbox.confirm_clear')}</p>
        </Modal.Body>
        <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowCustomModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => {resetHistory(); setShowCustomModal(false);}}>
          {t('ai_chatbox.clear')}
        </Button>
      </Modal.Footer>
      </>);
    setShowCustomModal(true);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() === '') return;

    const userText = inputValue.trim();

    const userMessage: MessageItem = {
      id: Date.now(),
      sender: 'user',
      text: userText
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue(''); 

    try {
      const response = await fetch('/api/ai_chatbox/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: userText, language: language })
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur: ${response.status}`);
      }

      const data = await response.json();

      // 3. Ajouter la réponse de Llama-3 retournée par Django à ton état
      const iaMessage: MessageItem = {
        id: Date.now() + 1,
        sender: 'ai',
        text: data.response // Récupère le champ 'response' du JSON renvoyé par Django
      };
      
      setMessages((prev) => [...prev, iaMessage]);

    } catch (error) {
      console.error(t('ai_chatbox.llm_error'), error);
    
      const errorMessage: MessageItem = {
        id: Date.now() + 1,
        sender: 'ai',
        text: t('ai_chatbox.error')
      };
      setMessages((prev) => [...prev, errorMessage]);
    }

    // setTimeout(() => {
    //   const iaMessage: MessageItem = {
    //     id: Date.now() + 1,
    //     sender: 'ai',
    //     text: `Tu as dit : "${userMessage.text}". Je prépare l'intégration avec Llama-3 !`
    //   };
    //   setMessages((prev) => [...prev, iaMessage]);
    // }, 1000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <>
      <Modal
          show={showCustomModal}
          onHide={() => setShowCustomModal(false)}
          link={linkModal}
          children={contentModal}
          isFooter={footerModal}
      />
      <div className="card shadow-lg aiChatBox">
        {/* Entête de la boîte */}
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={{ fontSize: '1rem' }}>{t('ai_chatbox.title_box')}</h5>
          <div className="d-flex align-items-center gap-2">
            {/* <button 
              onClick={handleClearHistory}
              className="btn btn-sm btn-link text-white p-0 me-1"
              title={t('ai_chatbox.clear_history', { defaultValue: 'Vider l\'historique' })}
              style={{ fontSize: '1rem', lineHeight: 1, textDecoration: 'none', opacity: 0.8 }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.8')}
            >
              🗑️
            </button> */}
            <button 
              onClick={handleClearHistory}
              className="btn btn-sm d-flex align-items-center justify-content-center p-0 border-0"
              title={t('ai_chatbox.clear_history', { defaultValue: 'Vider l\'historique' })}
              style={{ 
                width: '30px', 
                height: '30px', 
                borderRadius: '50%', 
                backgroundColor: 'rgba(255, 255, 255, 0.25)', // Cercle blanc/bleuté translucide sur le fond bg-primary
                fontSize: '0.9rem', 
                lineHeight: 1, 
                transition: 'background-color 0.2s ease, transform 0.1s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
              }}
            >
              🗑️
            </button>
            <button 
              onClick={onClose} 
              className="btn-close btn-close-white" 
              aria-label="Fermer"
            ></button>
          </div>
        </div>
        
        {/* Zone des messages dynamique */}
        <div className="card-body bg-light" style={{ flex: 1, overflowY: 'auto' }}>
          <p className="text-muted small text-center">{t('ai_chatbox.placeholder_box')}</p>
          
          {messages.map((msg) => (
            <div 
              key={msg.id} 
              className={`d-flex ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-2`}
            >
              <div 
                className={`p-2 rounded shadow-sm small ${
                  msg.sender === 'user' ? 'bg-primary text-white' : 'bg-white text-dark'
                }`}
                style={{ maxWidth: '80%', wordBreak: 'break-word' }}
              >
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Zone de saisie connectée aux états (CORRIGÉ ICI) */}
        <div className="card-footer bg-white border-top-0">
          <div className="input-group input-group-sm">
            <input 
              type="text" 
              className="form-control" 
              placeholder={t('ai_chatbox.input_placeholder')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button 
              className="btn btn-primary" 
              type="button"
              onClick={handleSendMessage}
            >
              {t('ai_chatbox.send')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}